import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

const initialState = {
  clientFeedbacks: [],
  currentClientFeedback: null,
  isLoading: false,
  error: null,
};

export const fetchClientFeedbacks = createAsyncThunk(
  'clientFeedback/fetchAll',
  async (patientId, { rejectWithValue }) => {
    try {
      const url = patientId ? `/client-feedbacks/${patientId}` : '/client-feedbacks';
      const response = await api.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch Client Feedback');
    }
  }
);

export const fetchClientFeedback = createAsyncThunk(
  'clientFeedback/fetchOne',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/client-feedbacks/show/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch Client Feedback');
    }
  }
);

export const createClientFeedback = createAsyncThunk(
  'clientFeedback/create',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.post('/client-feedbacks', formData);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) return rejectWithValue(error.response.data.errors);
      return rejectWithValue(error.response?.data?.error || 'Failed to create Client Feedback');
    }
  }
);

export const updateClientFeedback = createAsyncThunk(
  'clientFeedback/update',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/client-feedbacks/${id}`, formData);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) return rejectWithValue(error.response.data.errors);
      return rejectWithValue(error.response?.data?.error || 'Failed to update Client Feedback');
    }
  }
);

export const deleteClientFeedback = createAsyncThunk(
  'clientFeedback/delete',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/client-feedbacks/${id}`);
      return { id, message: response.data.message };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete Client Feedback');
    }
  }
);

const slice = createSlice({
  name: 'clientFeedback',
  initialState,
  reducers: {
    clearCurrentClientFeedback: (state) => { state.currentClientFeedback = null; },
    clearError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClientFeedbacks.pending, (s) => { s.isLoading = true; s.error = null; })
      .addCase(fetchClientFeedbacks.fulfilled, (s, a) => { s.isLoading = false; s.clientFeedbacks = a.payload; })
      .addCase(fetchClientFeedbacks.rejected, (s, a) => { s.isLoading = false; s.error = a.payload; })
      .addCase(fetchClientFeedback.pending, (s) => { s.isLoading = true; s.error = null; })
      .addCase(fetchClientFeedback.fulfilled, (s, a) => { s.isLoading = false; s.currentClientFeedback = a.payload; })
      .addCase(fetchClientFeedback.rejected, (s, a) => { s.isLoading = false; s.error = a.payload; })
      .addCase(createClientFeedback.pending, (s) => { s.isLoading = true; s.error = null; })
      .addCase(createClientFeedback.fulfilled, (s, a) => { s.isLoading = false; s.clientFeedbacks.unshift(a.payload); })
      .addCase(createClientFeedback.rejected, (s, a) => { s.isLoading = false; s.error = a.payload; })
      .addCase(updateClientFeedback.pending, (s) => { s.isLoading = true; s.error = null; })
      .addCase(updateClientFeedback.fulfilled, (s, a) => {
        s.isLoading = false;
        s.currentClientFeedback = a.payload;
        const i = s.clientFeedbacks.findIndex((r) => r.id === a.payload.id);
        if (i !== -1) s.clientFeedbacks[i] = a.payload;
      })
      .addCase(updateClientFeedback.rejected, (s, a) => { s.isLoading = false; s.error = a.payload; })
      .addCase(deleteClientFeedback.pending, (s) => { s.isLoading = true; s.error = null; })
      .addCase(deleteClientFeedback.fulfilled, (s, a) => { s.isLoading = false; s.clientFeedbacks = s.clientFeedbacks.filter((r) => r.id !== a.payload.id); })
      .addCase(deleteClientFeedback.rejected, (s, a) => { s.isLoading = false; s.error = a.payload; });
  },
});

export const { clearCurrentClientFeedback, clearError } = slice.actions;
export default slice.reducer;
